<template>
  <div>
    <div class="mb-2 d-flex align-items-center gap-2">
      <router-link
        :to="{ name: 'sale-prices-consignor-prices' }"
        class="d-flex align-items-center gap-2"
      >
        <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
        {{ t('Back to List') }}
      </router-link>
    </div>

    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="formObserver">
        <b-form ref="formEl" @submit.prevent="submit">
          <b-row>
            <b-col sm="12" md="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="provider">{{ t('Provider') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Provider')"
                  :rules="isCreate ? 'required' : ''"
                >
                  <template v-if="isCreate">
                    <v-select
                      id="provider"
                      v-model="values.providerId"
                      :options="providerOptions"
                      :reduce="item => item.value"
                      :placeholder="t('Make a Choice')"
                      label="label"
                    >
                      <span slot="no-options">{{ t('No records found') }}</span>
                    </v-select>
                  </template>
                  <template v-else>
                    <b-form-input id="provider_name" :value="providerName" disabled />
                  </template>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="12" md="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="customer">{{ t('Customer') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Customer')"
                  :rules="isCreate ? 'required' : ''"
                >
                  <template v-if="isCreate">
                    <v-select
                      id="customer"
                      v-model="values.customerId"
                      :options="customerOptions"
                      :reduce="item => item.value"
                      :placeholder="t('Make a Choice')"
                      label="label"
                    >
                      <span slot="no-options">{{ t('No records found') }}</span>
                    </v-select>
                  </template>
                  <template v-else>
                    <b-form-input id="customer_name" :value="customerName" disabled />
                  </template>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="12" md="4">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="price_value">{{ t('Return Rate') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Return Rate')"
                  rules="required|doubleOrInteger|greaterThanZero"
                >
                  <b-form-input
                    id="price_value"
                    v-model.number="values.price"
                    type="number"
                    :step="0.01"
                    :min="0"
                    :placeholder="isCreate ? t('Return-to-sender rate') : t('Enter Value')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <div
                class="d-flex gap-3 align-items-center justify-content-start mt-2"
                style="gap: 12px"
              >
                <b-button variant="primary" class="d-flex align-items-center" type="submit">
                  {{ submitting ? t('Please Wait') : t('Save') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>

<script>
import { BCol, BForm, BFormGroup, BRow, BButton, BOverlay, BFormInput } from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import vSelect from 'vue-select';
import useService from '@/auth/service/useService';

export default {
  name: 'EditSalePricesConsignorPrice',
  components: {
    BOverlay,
    BButton,
    BFormGroup,
    BRow,
    BCol,
    BForm,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    vSelect
  },
  mixins: [helper],
  data() {
    return {
      submitting: false,
      values: {
        providerId: null,
        customerId: null,
        price: null
      },
      providerOptions: [],
      customerOptions: [],
      isExisting: false,
      providerName: '',
      customerName: ''
    };
  },
  methods: {
    async loadData() {
      const { providerId, customerId } = this.$route.params;
      this.submitting = true;
      try {
        if (this.isCreate) {
          const providersRes = await useService
            .getAllProviders({ paginated: false })
            .catch(() => ({ data: [] }));
          this.providerOptions = (providersRes.data || []).map(item => ({
            value: item.id,
            label: item.name
          }));

          const customersRes = await useService
            .getAllCustomers({ paginated: false })
            .catch(() => ({ data: [] }));
          this.customerOptions = (customersRes.data || []).map(item => ({
            value: item.id,
            label: item.companyName || item.name
          }));

          this.values.providerId = null;
          this.values.customerId = null;
          this.values.price = null;
          this.providerName = '';
          this.customerName = '';
        } else if (providerId && customerId) {
          const feeRes = await useService.getCustomerReturnFeeForSale(
            parseInt(customerId),
            parseInt(providerId)
          );
          const fee = Array.isArray(feeRes?.data) ? feeRes.data[0] : feeRes?.data;
          this.isExisting = !!fee;
          this.values.providerId = fee?.provider?.id || parseInt(providerId);
          this.values.customerId = fee?.customer?.id || parseInt(customerId);
          this.values.price = fee?.price ?? null;

          this.providerName = fee?.provider?.name || '';
          this.customerName =
            (fee?.customer && (fee.customer.companyName || fee.customer.name)) || '';

          if (!fee) {
            // Eğer kayıt bulunamadıysa başlıkları tekil endpointlerden doldur
            const [providerRes, customerRes] = await Promise.all([
              useService.getProvider(parseInt(providerId)).catch(() => ({ data: {} })),
              useService.getCustomer(parseInt(customerId)).catch(() => ({ data: {} }))
            ]);
            this.providerName = providerRes?.data?.name || this.providerName;
            this.customerName =
              customerRes?.data?.companyName || customerRes?.data?.name || this.customerName;
          }
        } else {
          this.values.providerId = null;
          this.values.customerId = null;
          this.values.price = null;
          this.providerName = '';
          this.customerName = '';
        }
      } catch (messages) {
        if (typeof messages === 'object' && messages && messages.length) {
          messages.forEach(item => this.showErrorMessage(item.message));
        }
      } finally {
        this.submitting = false;
      }
    },
    submit() {
      this.$refs.formObserver.validate().then(async success => {
        if (!success) return;
        const { providerId, customerId } = this.$route.params;
        this.submitting = true;
        try {
          const args = { price: this.values.price };
          const currentProviderId = this.values.providerId || parseInt(providerId);
          const currentCustomerId = this.values.customerId || parseInt(customerId);
          if (this.isExisting) {
            await useService.editCustomerReturnFeeForSale(
              currentCustomerId,
              currentProviderId,
              args
            );
          } else {
            await useService.createCustomerReturnFeeForSale(
              currentCustomerId,
              currentProviderId,
              args
            );
          }
          this.showSuccessMessage(this.$t('Successful'));
          this.$router.push({ name: 'sale-prices-consignor-prices' });
        } catch (messages) {
          if (typeof messages === 'object' && messages && messages.length) {
            messages.forEach(item => this.showErrorMessage(item.message));
          }
        } finally {
          this.submitting = false;
        }
      });
    }
  },
  created() {
    this.loadData();
  },
  computed: {
    isCreate() {
      return !(this.$route.params.providerId && this.$route.params.customerId);
    }
  },
  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style lang="scss" scoped>
.form-group-label {
  margin-bottom: 0.5rem;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
